import { useCallback, useEffect, useMemo, useState } from "react";

import type { MetaDataPreMeetingPrepResponseStreamJson, UserErrorResponseJson } from "@performyard/service-contracts";

import { useInternalApiUser } from "../../../../api/hooks";
import { useAiQuery, useAiSettings } from "../../../../api/hooks/defs/ai";
import { useFeatureFlagQuery } from "../../../../hooks/useFeatureFlag";
import { usePreMeetingPrepGoals } from "./usePreMeetingPrepGoals";

import { useMeetingContext } from "../context";

const isSuccessResponse = (d: unknown): d is MetaDataPreMeetingPrepResponseStreamJson =>
	typeof d === "object" && d !== null && "status" in d && "data" in d;

const isErrorResponse = (d: unknown): d is UserErrorResponseJson =>
	typeof d === "object" && d !== null && "message" in d && !("data" in d);

type UsePreMeetingPrepResult = {
	data: MetaDataPreMeetingPrepResponseStreamJson | null;
	goalsData: ReturnType<typeof usePreMeetingPrepGoals>;
	isLoading: boolean;
	error: UserErrorResponseJson | null;
	isVisible: boolean;
	isOpen: boolean;
	handleOpen: () => void;
	handleClose: () => void;
	handleRegenerate: () => void;
	preMeetingPrepSettings: {
		suggestedAgendaItems?: boolean;
		goalsForDiscussion?: boolean;
		recentFeedback?: boolean;
		recentReviews?: boolean;
		surveyInsights?: boolean;
	} | null;
};

export const usePreMeetingPrep = (): UsePreMeetingPrepResult => {
	const { meeting, workspace, userId, isManagerDirectReportMeeting } = useMeetingContext();

	const [isOpen, setIsOpen] = useState(false);
	const [recreateCount, setRecreateCount] = useState(0);

	const { value: ffPreMeetingPrep } = useFeatureFlagQuery("ff-temp-is-ai-pre-meeting-prep-enabled", false);
	const { value: ffAiEnabled } = useFeatureFlagQuery("ff-is-ai-enabled", false);
	const { value: ffCoaching } = useFeatureFlagQuery("ff-is-coaching-enabled", false);
	const { value: ffMeetings } = useFeatureFlagQuery("ff-is-meetings-enabled", false);
	const { data: aiSettingsData } = useAiSettings();

	const preMeetingPrepEnabled = aiSettingsData?.preMeetingPrepEnabled ?? false;
	const preMeetingPrepSettings = aiSettingsData?.preMeetingPrepSettings ?? null;

	// Determine if the meeting is in the future and active
	const isFutureMeeting = useMemo(() => {
		if (!meeting?.startDateTime) return false;
		return new Date(meeting.startDateTime) > new Date();
	}, [meeting?.startDateTime]);

	const isActiveMeeting = meeting?.status === "active";

	// Find the direct report participant (the non-current user)
	const directReportId = useMemo(() => {
		if (!workspace?.participants || !userId) return undefined;
		const other = workspace.participants.find((p) => p.userId !== userId);
		return other?.userId;
	}, [workspace?.participants, userId]);

	// Pre-meeting prep is only shown to the manager side of the relationship.
	// Since isManagerDirectReportMeeting is true for both participants,
	// we need to verify the current user is the manager by checking their direct reports.
	const { data: currentUserData } = useInternalApiUser(userId, {
		enabled: Boolean(isManagerDirectReportMeeting) && Boolean(userId),
	});
	const isCurrentUserManager = useMemo(() => {
		if (!isManagerDirectReportMeeting || !directReportId) return false;
		const directReports = currentUserData?.directReports || [];
		return directReports.some((report) => report._id === directReportId);
	}, [isManagerDirectReportMeeting, directReportId, currentUserData?.directReports]);

	const isVisible = Boolean(
		ffPreMeetingPrep &&
		ffAiEnabled &&
		ffCoaching &&
		ffMeetings &&
		preMeetingPrepEnabled &&
		isCurrentUserManager &&
		isActiveMeeting &&
		isFutureMeeting
	);

	// AI query for pre-meeting prep
	const {
		data: queryData,
		isLoading,
		isFetching,
		isRefetching,
	} = useAiQuery<MetaDataPreMeetingPrepResponseStreamJson | UserErrorResponseJson>({
		type: "pre-meeting-prep",
		meetingId: meeting?._id || "",
		workspaceId: workspace?._id || "",
		findAdditionalContext: false,
		retry: recreateCount > 0 ? "true" : "false",
		responseStyle: "structured",
		recreateCount,
		enabled: isOpen && isVisible,
	});

	// Goals data (structured, no LLM)
	const goalsData = usePreMeetingPrepGoals({
		directReportId,
		enabled: isOpen && isVisible && (preMeetingPrepSettings?.goalsForDiscussion ?? false),
	});

	const inProgress = useMemo(() => {
		if (isLoading || isRefetching) return true;
		if (isSuccessResponse(queryData)) {
			return queryData.status === "in_progress";
		}
		return false;
	}, [queryData, isLoading, isRefetching]);

	const isError = useMemo((): boolean => {
		if (!queryData || inProgress) return false;
		return isErrorResponse(queryData);
	}, [queryData, inProgress]);

	const data = useMemo((): MetaDataPreMeetingPrepResponseStreamJson | null => {
		if (!queryData || isError || inProgress) return null;
		if (isSuccessResponse(queryData)) return queryData;
		return null;
	}, [queryData, isError, inProgress]);

	const error = useMemo((): UserErrorResponseJson | null => {
		if (!isError || !queryData) return null;
		if (isErrorResponse(queryData)) return queryData;
		return null;
	}, [queryData, isError]);

	const handleOpen = useCallback(() => setIsOpen(true), []);
	const handleClose = useCallback(() => setIsOpen(false), []);
	const handleRegenerate = useCallback(() => setRecreateCount((prev) => prev + 1), []);

	// Reset recreateCount when panel closes to ensure cached results are fetched on reopen
	useEffect(() => {
		if (!isOpen) {
			setRecreateCount(0);
		}
	}, [isOpen]);

	return {
		data,
		goalsData,
		isLoading: isLoading || isFetching || inProgress,
		error,
		isVisible,
		isOpen,
		handleOpen,
		handleClose,
		handleRegenerate,
		preMeetingPrepSettings,
	};
};
